/**
 * Error Utilities
 *
 * Centralized error parsing and formatting for consistent error handling across the app
 */

/**
 * Standard API error response from backend
 */
export interface ApiErrorResponse {
  success: false;
  message: string;
  errors?: Record<string, string | string[]>;
  source?: "zoho" | "internal" | "validation";
  error_details?: string;
  debug?: any;
}

/**
 * Parsed error for UI display
 */
export interface ParsedError {
  message: string;
  fieldErrors: Record<string, string>;
  globalErrors: string[];
  lineItemErrors: Record<number, string[]>;
  source: "zoho" | "internal" | "validation" | "network" | "auth" | "unknown";
  statusCode?: number;
  requiresAuth?: boolean;
}

/**
 * Parse API error from various error object structures
 * Handles Axios errors, backend errors, and network errors
 */
export function parseApiError(error: any, context?: ErrorContext): ParsedError {
  const result: ParsedError = {
    message: "An error occurred",
    fieldErrors: {},
    globalErrors: [],
    lineItemErrors: {},
    source: "unknown",
    statusCode: undefined,
    requiresAuth: false,
  };

  // Network error (no response from server)
  if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
    result.message =
      "Network error. Please check your connection and try again.";
    result.source = "network";
    return result;
  }

  // Extract error data from Axios response or fetch-based APIError
  const errorData = error.response?.data || error.data || error;
  const statusCode = error.response?.status ?? error.status;
  result.statusCode = statusCode;

  // Parse source
  if (errorData.source) {
    result.source = errorData.source;
  }

  // Check if authentication is required (401/403)
  if (statusCode === 401) {
    result.requiresAuth = true;
    result.message = "Your session has expired. Please login again.";
    result.source = "auth";
    return result; // Don't process field errors for auth issues
  }

  if (statusCode === 403) {
    result.message = "You don't have permission to perform this action.";
    result.source = "auth";
    return result; // Don't process field errors for permission issues
  }

  // Parse main message
  if (errorData.message) {
    result.message = getUserFriendlyMessage(
      cleanErrorMessage(errorData.message),
      statusCode,
      errorData.source
    );
  } else if (error.message) {
    result.message = getUserFriendlyMessage(
      cleanErrorMessage(error.message),
      statusCode,
      errorData.source
    );
  }

  // Parse field-level errors
  if (errorData.errors && typeof errorData.errors === "object") {
    const errors = errorData.errors;

    Object.entries(errors).forEach(([key, value]) => {
      const errorMessage = Array.isArray(value)
        ? value.join(", ")
        : String(value);

      // Check if it's a line item error (format: lineItem_0_account)
      const lineItemMatch = key.match(/^lineItem_(\d+)_(.+)$/);
      if (lineItemMatch) {
        const rowIndex = parseInt(lineItemMatch[1]);
        const fieldName = lineItemMatch[2];

        if (!result.lineItemErrors[rowIndex]) {
          result.lineItemErrors[rowIndex] = [];
        }
        result.lineItemErrors[rowIndex].push(
          `${getFieldDisplayName(fieldName, context)}: ${errorMessage}`
        );
      } else {
        // Regular field error
        result.fieldErrors[key] = errorMessage;
      }
    });
  }

  // Parse error_details if present (for partial success scenarios)
  if (errorData.error_details) {
    result.globalErrors.push(cleanErrorMessage(errorData.error_details));
  }

  return result;
}

/**
 * Get user-friendly error message based on error type and status code
 * Converts technical/dev errors into messages safe for end users
 */
export function getUserFriendlyMessage(
  message: string,
  statusCode?: number,
  source?: string
): string {
  // Handle server errors (5xx) - never show raw error
  if (statusCode && statusCode >= 500) {
    return "Something went wrong on our end. Please try again later.";
  }

  // Handle network-related errors
  if (message.toLowerCase().includes("network")) {
    return "Unable to connect. Please check your internet connection.";
  }

  // Handle timeout errors
  if (message.toLowerCase().includes("timeout")) {
    return "The request took too long. Please try again.";
  }

  // Handle connection refused/failed
  if (
    message.toLowerCase().includes("connection refused") ||
    message.toLowerCase().includes("connection failed")
  ) {
    return "Unable to reach the server. Please try again later.";
  }

  // For validation and Zoho errors, return the cleaned message as-is
  // (already cleaned by backend cleanErrorMessage)
  return message;
}

/**
 * Clean error message by removing technical details and Zoho branding
 * This is a frontend safety net - backend should already clean messages
 */
export function cleanErrorMessage(message: string): string {
  if (!message) return "An error occurred";

  let cleaned = message;

  // Remove any remaining Zoho references and "Api" prefix (safety net)
  const patterns = [
    /Zoho\s*Books\s*API\s*[Ee]rror\s*[:-]?\s*/gi,
    /Zoho\s*API\s*[Ee]rror\s*[:-]?\s*/gi,
    /Zoho\s*Books\s*[:-]?\s*/gi,
    /Zoho\s*[:-]?\s*/gi,
    /API\s*request\s*failed\s*[:-]?\s*/gi,
    /Error\s*[:-]\s*Error\s*[:-]\s*/gi,
    /^Api\s*[.:]\s*/i,
    /^API\s*[.:]\s*/i,
    /^Api\.?\s*/i,
    /^API\.?\s*/i,
  ];

  patterns.forEach((pattern) => {
    cleaned = cleaned.replace(pattern, "");
  });

  // Remove "Error:" prefix
  cleaned = cleaned.replace(/^Error\s*[:-]\s*/i, "");

  // Clean up whitespace
  cleaned = cleaned.replace(/\s+/g, " ").trim();

  // Ensure first character is uppercase
  if (cleaned.length > 0) {
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }

  return cleaned || "An error occurred";
}

/**
 * Format field errors for display
 * Converts technical field names to user-friendly labels
 */
export function formatFieldErrors(
  errors: Record<string, string>
): Record<string, string> {
  const formatted: Record<string, string> = {};

  Object.entries(errors).forEach(([key, value]) => {
    const displayName = getFieldDisplayName(key);
    formatted[displayName] = value;
  });

  return formatted;
}

/**
 * Context for error display (used for context-aware field names)
 */
export type ErrorContext =
  | "invoice"
  | "bill"
  | "customer"
  | "vendor"
  | "general";

/**
 * Get contextual field name based on the error context
 * Returns context-specific field names (e.g., "GST Number of the Vendor" vs "GST Number of the Customer")
 */
function getContextualFieldName(
  fieldKey: string,
  context: ErrorContext
): string | null {
  // GST Number context-aware naming
  if (
    fieldKey === "gst_no" ||
    fieldKey === "gstin" ||
    fieldKey === "gstin_vat_tax_id"
  ) {
    switch (context) {
      case "bill":
      case "vendor":
        return "GST Number of the Vendor";
      case "invoice":
      case "customer":
        return "GST Number of the Customer";
      default:
        return "GSTIN";
    }
  }

  // Add more context-aware field names here as needed

  return null;
}

/**
 * Get user-friendly display name for a field
 * Converts snake_case and camelCase to "Display Name"
 * Supports context-aware field names (e.g., different messages for invoices vs bills)
 */
export function getFieldDisplayName(
  fieldKey: string,
  context?: ErrorContext
): string {
  // Don't show field names for global errors (they don't need a label)
  if (fieldKey === "_global" || fieldKey.startsWith("global_")) {
    return "";
  }

  // Context-aware field names
  if (context) {
    const contextualFieldName = getContextualFieldName(fieldKey, context);
    if (contextualFieldName) {
      return contextualFieldName;
    }
  }

  // API/global errors - no field label, just the message
  if (fieldKey === "api") {
    return "";
  }

  const fieldNameMap: Record<string, string> = {
    // Common fields
    date: "Date",
    notes: "Notes",
    journalNumber: "Journal Number",
    journal_number: "Journal Number",
    referenceNumber: "Reference Number",
    reference_number: "Reference Number",
    currencyId: "Currency",
    currency_id: "Currency",

    // Recurring journal fields
    recurringJournalName: "Recurring Journal Name",
    recurringFrequency: "Frequency",
    recurringInterval: "Number of Days",
    recurringStartDate: "Start Date",
    recurringEndDate: "End Date",

    // Line item fields
    account: "Account",
    account_id: "Account",
    account_type: "Account Type",
    contact: "Contact",
    contact_id: "Contact",
    description: "Description",
    amount: "Amount",
    debit: "Debit",
    credit: "Credit",

    // Customer fields
    customer: "Customer Verification",
    customer_name: "Customer Name",
    customer_id: "Customer",
    customerCurrency: "Customer Currency",
    contact_name_1: "Primary Contact Name",
    contact_number_1: "Primary Contact Number",
    contact_email_1: "Primary Contact Email",
    contact_email_2: "Secondary Contact Email",
    billing_address: "Billing Address",
    city: "City",
    state: "State",
    country: "Country",
    postal_code: "Postal Code",
    credit_limit: "Credit Limit",
    payment_terms: "Payment Terms",
    gst_treatment: "GST Treatment",
    gstin_vat_tax_id: "GSTIN",
    pan_tax_registration_no: "PAN",

    // Invoice/Bill specific fields
    currency: "Currency",
    lineItemTax: "Line Item Tax",
    invoice_number: "Invoice Number",
    invoice_date: "Invoice Date",
    due_date: "Due Date",

    // Vendor fields
    vendor: "Vendor Verification",
    vendor_id: "Vendor",
    vendorCurrency: "Vendor Currency",
    vendor_legal_name: "Vendor Legal Name",
    vendor_legal_entity: "Legal Entity",
    type_of_vendor: "Vendor Type",
    business_category: "Business Category",
    registered_office_address: "Registered Office Address",
    primary_contact_name: "Primary Contact Name",
    primary_contact_phone: "Primary Contact Phone",
    primary_contact_email: "Primary Contact Email",
    bank_account_number: "Bank Account Number",
    bank_name: "Bank Name",
    beneficiary_name: "Beneficiary Name",
    branch: "Branch",
    ifsc_swift_code: "IFSC/SWIFT Code",
    msme_flag: "MSME Status",
    udyam_number: "Udyam Number",
    tds_applicable: "TDS Applicable",
    tds_tax_id: "TDS Tax Rate",
    gst_category: "GST Category",
    gstin: "GSTIN",
    company_registration_number: "Company Registration Number",

    // Other common fields
    balance: "Balance",
    status: "Status",
  };

  // Return mapped name if exists
  if (fieldNameMap[fieldKey]) {
    return fieldNameMap[fieldKey];
  }

  // Convert snake_case to Title Case
  const titleCase = fieldKey
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1") // Handle camelCase
    .split(" ")
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

  return titleCase;
}

/**
 * Categorize errors into field-level, line-item, and global errors
 */
export function categorizeErrors(errors: Record<string, string>): {
  fieldErrors: Record<string, string>;
  lineItemErrors: Record<number, Record<string, string>>;
  globalErrors: string[];
} {
  const fieldErrors: Record<string, string> = {};
  const lineItemErrors: Record<number, Record<string, string>> = {};
  const globalErrors: string[] = [];

  Object.entries(errors).forEach(([key, value]) => {
    // Check if it's a line item error (format: lineItem_0_account)
    const lineItemMatch = key.match(/^lineItem_(\d+)_(.+)$/);

    if (lineItemMatch) {
      const rowIndex = parseInt(lineItemMatch[1]);
      const fieldName = lineItemMatch[2];

      if (!lineItemErrors[rowIndex]) {
        lineItemErrors[rowIndex] = {};
      }
      lineItemErrors[rowIndex][fieldName] = value;
    } else if (key === "balance" || key === "global" || key === "_global") {
      // Global errors (like balance mismatch)
      globalErrors.push(value);
    } else {
      // Regular field error
      fieldErrors[key] = value;
    }
  });

  return { fieldErrors, lineItemErrors, globalErrors };
}

/**
 * Check if errors object has any errors
 */
export function hasErrors(
  errors: Record<string, string> | null | undefined
): boolean {
  if (!errors) return false;
  return Object.keys(errors).length > 0;
}

/**
 * Get first error field key (useful for auto-scroll)
 */
export function getFirstErrorField(
  errors: Record<string, string>
): string | null {
  const keys = Object.keys(errors);
  return keys.length > 0 ? keys[0] : null;
}

/**
 * Format errors for error banner display
 * Returns array of { field: string, message: string } for rendering
 * Cleans all messages to remove Api/API prefixes and other technical noise
 */
export function formatErrorsForDisplay(
  errors: Record<string, string>,
  context?: ErrorContext
): Array<{ field: string; message: string }> {
  return Object.entries(errors).map(([key, value]) => ({
    field: getFieldDisplayName(key, context),
    message: cleanErrorMessage(value),
  }));
}

/**
 * Extract error message from various error structures
 * Handles legacy error formats from before backend standardization
 */
export function extractErrorMessage(error: any): string {
  if (!error) return "An unknown error occurred";

  // If error is already a string
  if (typeof error === "string") {
    return cleanErrorMessage(error);
  }

  // Check for response.data.message (Axios structure)
  if (error.response?.data?.message) {
    return cleanErrorMessage(error.response.data.message);
  }

  // Check for response.data.error
  if (error.response?.data?.error) {
    return cleanErrorMessage(error.response.data.error);
  }

  // Check for error.message
  if (error.message) {
    return cleanErrorMessage(error.message);
  }

  // Check for error.error
  if (error.error) {
    return cleanErrorMessage(error.error);
  }

  // Fallback
  return "An unknown error occurred";
}

/**
 * Create error summary text from errors object
 * Used for displaying total error count or summary
 */
export function getErrorSummary(errors: Record<string, string>): string {
  const count = Object.keys(errors).length;
  if (count === 0) return "";
  if (count === 1) return "1 error found";
  return `${count} errors found`;
}
